import { roundToCents } from "utils/bookingCalculations"

const isCanceledEntry = (entry) => entry.data?.rawStatus === "canceled"

// An order's experiences that still count toward its money: loaded and not canceled.
export const getActiveOrderExperiences = (experiences) => experiences.filter((entry) => entry.data?.payments && !isCanceledEntry(entry))

// An order shows one Price Change line: an order-level adjustment is recorded on every booking, so
// listing each booking's share would repeat it. Its tooltip names each different reason once.
const combinePriceChanges = (entries) => {
  const adjustments = entries.flatMap((entry) => entry.data.payments.priceAdjustments || [])
  const amount = roundToCents(adjustments.reduce((total, adjustment) => total + adjustment.amount, 0))
  if (!amount) return []
  const reasons = [...new Set(adjustments.map((adjustment) => adjustment.reason).filter(Boolean))]
  return [{ id: "order-price-change", amount, reason: reasons.join(" · ") || null }]
}

// One Payments panel for a whole order, in the shape PaymentsPanel reads for a single booking: each
// experience as a line (its base price), then the order's subtotal, taxes & fees, price changes,
// total, card fee and balance summed over its active experiences. A canceled experience is left out,
// as its money no longer belongs to the reservation. Once every experience is canceled, the panel
// shows what was booked, as a single canceled booking's panel does.
export const buildOrderPayments = (experiences) => {
  const active = getActiveOrderExperiences(experiences)
  const isCanceled = active.length === 0 && experiences.some((entry) => entry.data?.payments)
  const counted = isCanceled ? experiences.filter((entry) => entry.data?.payments) : active
  const sum = (read) => roundToCents(counted.reduce((total, entry) => total + (Number(read(entry.data.payments)) || 0), 0))

  // Fees of the same name across experiences become one line.
  const feesByLabel = new Map()
  counted.forEach((entry) =>
    (entry.data.payments.taxesAndFeesBreakdown || []).forEach((fee) => {
      feesByLabel.set(fee.label, roundToCents((feesByLabel.get(fee.label) || 0) + (Number(fee.amount) || 0)))
    })
  )

  const remainingBalance = sum((payments) => payments.remainingBalance)
  const isPaidInFull = !isCanceled && active.length > 0 && active.every((entry) => entry.data.payments.isPaidInFull)
  const discountAmount = sum((payments) => payments.discount?.amount)

  return {
    depositLabel: (isCanceled && "Reservation canceled") || (isPaidInFull ? "Total payment collected" : "Balance due"),
    paymentTag: (isCanceled && { label: "Canceled", color: "red" }) || {
      label: isPaidInFull ? "Paid in Full" : "Balance Due",
      color: isPaidInFull ? "green" : "orange"
    },
    isPaidInFull,
    experienceLines: counted.map((entry) => ({
      key: entry.uuid,
      label: entry.data.experience?.title || "",
      amount: Number(entry.data.payments.subtotal?.amount) || 0
    })),
    subtotal: { amount: sum((payments) => payments.subtotal?.amount) },
    taxesAndFees: sum((payments) => payments.taxesAndFees),
    taxesAndFeesBreakdown: [...feesByLabel].map(([label, amount]) => ({ label, amount })),
    discount: discountAmount > 0 ? { amount: discountAmount } : null,
    priceAdjustments: combinePriceChanges(counted),
    bookingTotal: sum((payments) => payments.bookingTotal ?? payments.total),
    total: sum((payments) => payments.total),
    processingFee: sum((payments) => payments.processingFee),
    totalCharged: sum((payments) => payments.totalCharged ?? payments.total),
    remainingBalance,
    refundDue: remainingBalance < 0 ? Math.abs(remainingBalance) : 0,
    paidAmount: sum((payments) => payments.paidAmount),
    outstandingBalance: sum((payments) => payments.outstandingBalance),
    isRefundPending: counted.some((entry) => entry.data.payments.isRefundPending),
    refundPendingAmount: sum((payments) => payments.refundPendingAmount)
  }
}

// Every payment of the order's experiences in one list, newest first, each naming its experience.
export const mergeOrderPaymentHistory = (historyByUuid, experiences) =>
  experiences
    .flatMap((entry) =>
      (historyByUuid[entry.uuid] || []).map((payment) => ({
        ...payment,
        id: `${entry.uuid}-${payment.id}`,
        experienceTitle: entry.data?.experience?.title || entry.booking?.title || ""
      }))
    )
    .sort((first, second) => String(second.occurredAt || "").localeCompare(String(first.occurredAt || "")))
